import React, { useCallback, useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import BookOpenIcon from '@atlaskit/icon-lab/core/book-open';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';

import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { READ_TIME_SEGMENT_KEY } from '@confluence/browser-metrics/entry-points/metric-keys.ts';

export const i18n: {
	abbreviatedReadTime: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readTime: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	iconLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	tooltipDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	wordCount: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	abbreviatedReadTime: {
		id: 'read-time.by-line.abbreviated-estimated-read-time',
		defaultMessage: '{readTime} min',
		description: 'Abbreviated estimated time to read this page',
	},
	readTime: {
		id: 'read-time.by-line.estimated-read-time',
		defaultMessage: '{readTime} min read',
		description:
			'The text is shown in the byline area of a Confluence page, indicating the estimated reading time. The placeholder {readTime} will be substituted with the calculated number of minutes required to read the page content.',
	},
	iconLabel: {
		id: 'read-time.by-line.icon-label',
		defaultMessage: 'Read time',
		description: 'Accessibility label for the book icon next to the read time in the byline',
	},
	tooltipDescription: {
		id: 'read-time.tooltip-description',
		defaultMessage: 'Estimated read time',
		description:
			'Tooltip shown when hovering over the read time indicator in the page byline, explaining the estimate.',
	},
	wordCount: {
		id: 'read-time.word-count',
		defaultMessage: '{wordCount} {wordCount, plural, one {word} other {words}}',
		description:
			'This message informs the user about the total number of words used in their content.',
	},
});

const abbreviatedReadTimeStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.050',
	width: 'max-content',
	height: '24px',
});

const readTimeWithBulletStyles = xcss({
	display: 'inline-flex',
});

const baseStyledReadTime = css({
	marginRight: token('space.050'),
	paddingLeft: token('space.075'),
	color: token('color.text.subtle'),
});

const detailsPanelStyledReadTime = css({
	marginRight: 0,
	color: token('color.text'),
	display: 'flex',
	alignItems: 'center',
	'&::before': {
		content: '•',
		display: 'inline-block',
		color: token('color.text'),
		paddingRight: token('space.050'),
		paddingLeft: token('space.050'),
		textDecoration: 'none',
	},
});

const underlineHoverStyles = xcss({
	font: 'font.body',
	':hover': {
		textDecoration: 'underline',
	},
});

const abbreviatedReadTimeBadgeStyles = css({
	marginRight: token('space.0'),

	paddingLeft: token('space.050'),
	paddingRight: token('space.050'),

	cursor: 'pointer',
	height: '24px',
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		borderRadius: token('radius.small'),
	},
});

const readTimeBullet = css({
	'&::before': {
		content: '•',
		display: 'inline-block',
		color: token('color.text.subtle'),
		paddingRight: token('space.100'),
		textDecoration: 'none',
	},
});

type ReadTimeComponentType = {
	pageId: string;
	readTime: number | null;
	isAbbreviatedReadTime?: boolean;
	isInEditor?: boolean;
	isDetailsPanel?: boolean;
	showWordCount?: boolean;
	wordCount?: number | null;
};

export function ReadTimeComponent({
	pageId,
	readTime,
	isAbbreviatedReadTime = true,
	isInEditor,
	isDetailsPanel,
	wordCount,
	showWordCount,
}: ReadTimeComponentType): React.JSX.Element {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const fireTooltipEvent = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'page',
				action: 'displayed',
				actionSubject: 'tooltip',
				actionSubjectId: 'estimatedReadTime',
				attributes: {
					isInEditor: !!isInEditor,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isInEditor]);

	useEffect(() => {
		if (readTime) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'confluence-frontend',
					action: 'displayed',
					actionSubject: 'readTime',
					actionSubjectId: pageId,
					attributes: {
						readTime,
						isInEditor: !!isInEditor,
					},
				},
			}).fire();
		}
	}, [pageId, readTime, createAnalyticsEvent, isInEditor]);

	const readTimeMessage = isAbbreviatedReadTime ? i18n.abbreviatedReadTime : i18n.readTime;

	return (
		// styled elements cannot be the primary component as ssr-placeholder attributes
		// will erroneously be added to the <style> components during SSR. To fix this,
		// this component is wrapped in a div with an inline style
		<div
			data-vc="read-time"
			data-testid="read-time-component"
			{...ssrPlaceholderIdProp}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={{ display: 'inline-flex' }}
		>
			{showWordCount && wordCount != null && (
				<Box xcss={[isDetailsPanel && underlineHoverStyles]}>
					{formatMessage(i18n.wordCount, {
						wordCount,
					})}
				</Box>
			)}
			{!!readTime && (
				<span
					css={[
						!isAbbreviatedReadTime && readTimeBullet,
						isDetailsPanel ? detailsPanelStyledReadTime : baseStyledReadTime,
						isAbbreviatedReadTime && abbreviatedReadTimeBadgeStyles,
					]}
				>
					<Tooltip
						tag="span"
						content={<FormattedMessage {...i18n.tooltipDescription} />}
						onShow={fireTooltipEvent}
						hideTooltipOnClick
						delay={0}
					>
						<Box
							xcss={[
								isAbbreviatedReadTime ? abbreviatedReadTimeStyles : readTimeWithBulletStyles,
								isDetailsPanel && underlineHoverStyles,
							]}
						>
							{isAbbreviatedReadTime && <BookOpenIcon label={formatMessage(i18n.iconLabel)} />}
							<FormattedMessage {...readTimeMessage} values={{ readTime }} />
						</Box>
					</Tooltip>
				</span>
			)}
			<PageSegmentLoadEnd metricKey={READ_TIME_SEGMENT_KEY} />
		</div>
	);
}
